import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from "firebase/app-check";
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager, type Firestore } from "firebase/firestore";
import { getEnv, getMissingFirebaseConfig } from "@/app/config/env";

let app: FirebaseApp | undefined;
let firestore: Firestore | undefined;
let appCheck: AppCheck | undefined;

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

function initializeFirebaseAppCheck(firebaseApp: FirebaseApp): void {
  if (appCheck || typeof window === "undefined") return;

  const config = getEnv();
  const siteKey = config.appCheck.siteKey?.trim();
  if (!siteKey) return;

  if (config.appCheck.debugToken) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = config.appCheck.debugToken;
  }

  appCheck = initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export function getFirebaseApp(): FirebaseApp {
  if (app) {
    initializeFirebaseAppCheck(app);
    return app;
  }

  const existing = getApps();
  if (existing.length > 0) {
    app = existing[0] as FirebaseApp;
    initializeFirebaseAppCheck(app);
    return app;
  }

  const missingConfig = getMissingFirebaseConfig();
  if (missingConfig.length > 0) {
    throw new Error(`Firebase is not configured. Missing: ${missingConfig.join(", ")}`);
  }

  const config = getEnv();
  app = initializeApp(config.firebase);
  initializeFirebaseAppCheck(app);
  return app;
}

export function getFirestoreDb(): Firestore {
  if (firestore) return firestore;
  const firebaseApp = getFirebaseApp();

  try {
    firestore = initializeFirestore(firebaseApp, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch {
    // initializeFirestore throws if already called — fall back to getFirestore
    firestore = getFirestore(firebaseApp);
  }

  return firestore;
}
